import { useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { FinancialBody } from 'modules/arena/reports/model/bodies';
import { DATE_BASIS_LABEL } from 'modules/arena/reports/model/areas';
import { formatMoney } from 'modules/arena/reports/model/format';
import { formatPeriod } from 'modules/arena/reports/model/dates';
import { KpiGrid } from 'modules/arena/reports/components/KpiCard';
import { Panel } from 'modules/arena/reports/components/Panel';
import { MoneyBasisToggle } from 'modules/arena/reports/components/MoneyBasisToggle';
import { DataTable } from 'modules/arena/reports/components/DataTable';
import { DetailDrawer } from 'modules/arena/reports/components/DetailDrawer';
import { AreaProps, barsToReais, BASIS_TITLE, formatReais, GROUPING_LABEL, toReais } from 'modules/arena/reports/areas/shared';
import { RevenueTrendChart } from 'shared/components/charts/RevenueTrendChart';
import { MagnitudeBarList } from 'shared/components/charts/MagnitudeBarList';
import { cn } from 'shared/utils/cn';

export function FinancialArea({ body, meta, controller }: AreaProps<FinancialBody>) {
  const { moneyBasis, setMoneyBasis, applied, changeTable, tableLoading, source, area } = controller;
  const [selected, setSelected] = useState<string | null>(null);
  const basisLabel = DATE_BASIS_LABEL[body.composition.basis];
  const seriesTitle = `${moneyBasis === 'bruto' ? (body.series.basis === 'venda' || body.series.basis === 'vencimento' ? 'Valor bruto' : 'Recebido (bruto)') : 'Líquido da arena'} ${GROUPING_LABEL[applied.common.grouping]}`;

  return (
    <div className="flex flex-col gap-8">
      <p className="flex items-start gap-2 rounded-xl border border-border/60 bg-surface px-4 py-3 text-sm text-ink">
        <CalendarRange className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-dark" aria-hidden="true" />
        <span>
          A tabela, a composição e o gráfico estão posicionados pela <strong>{basisLabel}</strong>. Os blocos abaixo indicam a
          própria data-base em cada indicador. Para mudar, use o filtro “Data de referência”.
        </span>
      </p>

      {body.groups.map((group) => (
        <section key={group.id} aria-labelledby={`grupo-${group.id}`}>
          <h3 id={`grupo-${group.id}`} className="font-heading text-base font-semibold text-ink">
            {group.title}
          </h3>
          <p className="mb-3 text-sm text-ink-secondary">{group.description}</p>
          <KpiGrid metrics={group.metrics} meta={meta} />
        </section>
      ))}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_1.3fr]">
        <Panel title="Do bruto ao líquido da arena" subtitle={`Pedidos posicionados pela ${basisLabel}`}>
          <dl className="divide-y divide-border/50">
            {body.composition.rows.map((row) => (
              <div key={row.id} className={cn('flex items-start justify-between gap-4 py-2.5', row.kind === 'total' && 'pt-3')}>
                <dt>
                  <span className={cn('text-sm', row.kind === 'total' ? 'font-semibold text-ink' : 'text-ink')}>{row.label}</span>
                  <span className="block text-xs text-ink-secondary">{row.help}</span>
                </dt>
                <dd className={cn('whitespace-nowrap text-right text-sm tabular-nums', row.kind === 'total' ? 'font-bold text-primary-dark' : row.cents < 0 ? 'text-danger' : 'text-ink')}>
                  {formatMoney(row.cents)}
                </dd>
              </div>
            ))}
          </dl>
          <p className="rounded-lg bg-chip px-3 py-2 text-xs text-ink">
            <strong>Taxas cobradas dos clientes:</strong> {formatMoney(body.composition.clientPaidFeesCents)}. Pagas pelos clientes como taxa
            de serviço, além do valor do pedido — não saem do valor da arena e não entram no líquido acima.
          </p>
        </Panel>

        <Panel
          title={seriesTitle}
          subtitle={`Pela ${basisLabel} · ${formatPeriod(meta.period.start, meta.period.end)}`}
          actions={<MoneyBasisToggle value={moneyBasis} onChange={setMoneyBasis} />}
        >
          <RevenueTrendChart
            points={toReais(body.series[moneyBasis])}
            comparePoints={body.series.previous ? toReais(body.series.previous[moneyBasis]) : undefined}
            compareLabel="Período anterior"
            formatValue={formatReais}
            ariaLabel={seriesTitle}
          />
          <div className="grid grid-cols-1 gap-6 border-t border-border/50 pt-4 md:grid-cols-2">
            <div>
              <h4 className="mb-3 text-sm font-semibold text-ink">{BASIS_TITLE[moneyBasis]} por produto</h4>
              <MagnitudeBarList items={barsToReais(body.byProduct[moneyBasis])} formatValue={formatReais} />
            </div>
            <div>
              <h4 className="mb-3 text-sm font-semibold text-ink">{BASIS_TITLE[moneyBasis]} por meio de pagamento</h4>
              <MagnitudeBarList items={barsToReais(body.byMethod[moneyBasis])} formatValue={formatReais} />
            </div>
          </div>
        </Panel>
      </div>

      <DataTable
        title="Pedidos do período"
        table={body.table}
        loading={tableLoading}
        onChange={changeTable}
        onRowSelect={(row) => setSelected(row.id)}
        footnote="“Taxas da arena”: plataforma + processador quando a arena paga. Líquido é sobre o valor já recebido. Parcialmente pagos mostram o saldo no detalhe."
      />
      <DetailDrawer source={source} area={area} rowId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
